import React, { useState, useEffect } from 'react';
import { useLoader } from '../../../../../base/context/LoaderProvider';
import { ToastTypes, useMessage } from '../../../../../base/context/MessageProvider';
import { orderApiService } from '../../../../../service/admin/ordersApiService';
import { useNavigate } from 'react-router-dom';
import { FiX } from 'react-icons/fi';
import './style.css';

const ManualOrderForm = ({ isOpen, onClose, onSuccess }) => {
  const loader = useLoader();
  const message = useMessage();
  const navigate = useNavigate();

  // Initial form data (for reset)
  const getInitialFormData = () => ({
    // Order Info
    orderId: '',
    marketplace: 'MANUAL',
    status: 'processing',
    orderDate: new Date().toISOString().split('T')[0],
    paidDate: '',
    transactionId: '',
    orderType: 'dropshipping', // dropshipping or wholesale

    // Seller
    sellerId: '',
    sellerName: '',
    sellerEmail: '',

    // Flags
    ticket: false,
    orderExport: false,
    fromFailedOrder: false,

    // Shipping Info
    recipientName: '',
    shippingPhoneNumber: '',
    shippingAddress1: '',
    shippingAddress2: '',
    shippingAddress3: '',
    shippingCity: '',
    shippingState: '',
    shippingPostalCode: '',

    // Billing Info
    useSameAddressForBilling: true,
    billingName: '',
    billingPhone: '',
    billingAddress1: '',
    billingAddress2: '',
    billingAddress3: '',
    billingCity: '',
    billingState: '',
    billingPostalCode: '',

    // Shipping & GST
    totalShippingCharges: '',
    gstOnShipping: '',

    // Order Details (Array)
    orderDetails: [
      {
        orderItemId: '',
        sku: '',
        quantity: 1,
        consumerPrice: '',
        variant: {
          skuId: '',
          price: '',
          weight: '',
          ls: '',
          finalPrice: '',
          adminCustomPrice: '',
        },
      },
    ],

    // Breakdown (Array)
    breakdown: [],

    // Order Status (Array)
    orderStatus: [{ status: 'processing', date: new Date().toISOString() }],

    // Marketplace fields
    mkp_current_total_price: '',
    mkp_financial_status: '',
    mkp_current_total_tax: '',
    mkp_current_subtotal_price: '',

    // Optional arrays
    mkpOrderStatusHistory: [],
  });

  const [formData, setFormData] = useState(getInitialFormData());

  // Reset form function
  const resetForm = () => {
    setFormData(getInitialFormData());
    setShowSellerSearch(false);
    setSellerSearchQuery('');
    setSellerSearchResults([]);
    setProductSearchSku('');
    setSearchedProduct(null);
    setShowVariantSelector(false);
    setAvailablePricingModes({ dropshipping: true, wholesale: true });
    setVariantQuantities({});
    setSelectedPricingMode('dropshipping');
  };

  // Handle modal close with reset
  const handleClose = () => {
    resetForm();
    onClose();
  };

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      resetForm();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const [showSellerSearch, setShowSellerSearch] = useState(false);
  const [sellerSearchQuery, setSellerSearchQuery] = useState('');
  const [sellerSearchResults, setSellerSearchResults] = useState([]);
  const [searchingUsers, setSearchingUsers] = useState(false);

  // Product search state
  const [productSearchSku, setProductSearchSku] = useState('');
  const [searchingProduct, setSearchingProduct] = useState(false);
  const [searchedProduct, setSearchedProduct] = useState(null);
  const [showVariantSelector, setShowVariantSelector] = useState(false);
  const [availablePricingModes, setAvailablePricingModes] = useState({ dropshipping: true, wholesale: true });
  const [variantQuantities, setVariantQuantities] = useState({}); // Track quantity for each variant
  const [selectedPricingMode, setSelectedPricingMode] = useState('dropshipping'); // For variant display tabs

  // Update existing items' finalPrice when pricing mode changes
  // Only update items that don't have a pricingMode set (for backward compatibility)
  // Items with pricingMode set will keep their original mode
  const updateItemsForPricingMode = newMode => {
    setFormData(prev => {
      const updatedOrderDetails = prev.orderDetails.map(detail => {
        if (detail.variant && detail.variant.skuId) {
          // Only update items that don't have a pricingMode (old items)
          // Items with pricingMode should keep their original mode
          if (detail.variant.pricingMode) {
            return detail; // Keep original pricing mode
          }

          // Find the variant from searchedProduct to get the correct prices
          let variantData = null;
          if (searchedProduct && searchedProduct.product && searchedProduct.product.variations) {
            variantData = searchedProduct.product.variations.find(v => String(v.skuId) === String(detail.variant.skuId));
          }

          if (variantData) {
            const newFinalPrice = newMode === 'wholesale' ? variantData.wholesaleFinalPrice || 0 : variantData.finalPrice || 0;

            return {
              ...detail,
              variant: {
                ...detail.variant,
                finalPrice: newFinalPrice,
              },
            };
          }
        }
        return detail;
      });
      return {
        ...prev,
        orderDetails: updatedOrderDetails,
      };
    });
  };

  // Handle basic input changes
  const handleInputChange = e => {
    const { name, value, type, checked } = e.target;

    // If order type changes, update consumer prices for all order details
    if (name === 'orderType') {
      setFormData(prev => {
        const updatedOrderDetails = prev.orderDetails.map(detail => {
          if (detail.variant) {
            const newPrice = value === 'dropshipping' ? detail.variant.finalPrice || 0 : detail.variant.wholesaleFinalPrice || 0;
            return {
              ...detail,
              consumerPrice: newPrice,
            };
          }
          return detail;
        });
        return {
          ...prev,
          [name]: value,
          orderDetails: updatedOrderDetails,
        };
      });
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value,
      }));
    }
  };

  // Handle order detail changes
  const handleOrderDetailChange = (index, field, value) => {
    const newOrderDetails = [...formData.orderDetails];
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      newOrderDetails[index][parent] = {
        ...newOrderDetails[index][parent],
        [child]: value,
      };
    } else {
      newOrderDetails[index][field] = value;
    }
    setFormData(prev => ({ ...prev, orderDetails: newOrderDetails }));
  };

  // Add order detail item
  const addOrderDetail = () => {
    setFormData(prev => ({
      ...prev,
      orderDetails: [
        ...prev.orderDetails,
        {
          orderItemId: '',
          sku: '',
          quantity: 1,
          consumerPrice: '',
          variant: {
            skuId: '',
            price: '',
            weight: '',
            ls: '',
            finalPrice: '',
            adminCustomPrice: '',
          },
        },
      ],
    }));
  };

  // Remove order detail item
  const removeOrderDetail = index => {
    if (formData.orderDetails.length > 1) {
      setFormData(prev => ({
        ...prev,
        orderDetails: prev.orderDetails.filter((_, i) => i !== index),
      }));
    }
  };

  // Handle breakdown changes
  const handleBreakdownChange = (index, field, value) => {
    const newBreakdown = [...formData.breakdown];
    newBreakdown[index] = {
      ...newBreakdown[index],
      [field]: value,
    };
    setFormData(prev => ({ ...prev, breakdown: newBreakdown }));
  };

  // Add breakdown item
  const addBreakdownItem = () => {
    setFormData(prev => ({
      ...prev,
      breakdown: [
        ...prev.breakdown,
        {
          productId: '',
          pricingMode: 'dropshipping',
          LP: '',
          LC: '',
          LS: '',
          GST_PR: '',
          GST_FR: '',
          FBP: '',
        },
      ],
    }));
  };

  // Remove breakdown item
  const removeBreakdownItem = index => {
    setFormData(prev => ({
      ...prev,
      breakdown: prev.breakdown.filter((_, i) => i !== index),
    }));
  };

  // Handle order status changes
  const handleOrderStatusChange = (index, field, value) => {
    const newOrderStatus = [...formData.orderStatus];
    newOrderStatus[index][field] = value;
    setFormData(prev => ({ ...prev, orderStatus: newOrderStatus }));
  };

  // Add order status item
  const addOrderStatus = () => {
    setFormData(prev => ({
      ...prev,
      orderStatus: [
        ...prev.orderStatus,
        {
          status: '',
          date: new Date().toISOString(),
        },
      ],
    }));
  };

  // Remove order status item
  const removeOrderStatus = index => {
    if (formData.orderStatus.length > 1) {
      setFormData(prev => ({
        ...prev,
        orderStatus: prev.orderStatus.filter((_, i) => i !== index),
      }));
    }
  };

  // Search users
  const handleSellerSearch = async query => {
    setSellerSearchQuery(query);

    if (query.trim().length < 2) {
      setSellerSearchResults([]);
      return;
    }

    setSearchingUsers(true);
    try {
      const res = await orderApiService.searchUsers(query);
      if (res.status.code === 200) {
        setSellerSearchResults(res.data || []);
      } else {
        setSellerSearchResults([]);
      }
    } catch (err) {
      message.showToast('Error searching users', ToastTypes.ERROR);
      setSellerSearchResults([]);
    } finally {
      setSearchingUsers(false);
    }
  };

  // Select seller
  const selectSeller = user => {
    setFormData(prev => ({
      ...prev,
      sellerId: user._id,
      sellerName: user.firstName || user.userName || user.email,
      sellerEmail: user.email,
    }));
    setShowSellerSearch(false);
    setSellerSearchQuery('');
    setSellerSearchResults([]);
  };

  // Clear seller selection
  const clearSeller = () => {
    setFormData(prev => ({
      ...prev,
      sellerId: '',
      sellerName: '',
      sellerEmail: '',
    }));
  };

  // Search product by SKU
  const handleProductSearch = async () => {
    if (!productSearchSku.trim()) {
      message.showToast('Please enter a SKU', ToastTypes.ERROR);
      return;
    }

    setSearchingProduct(true);
    try {
      const res = await orderApiService.searchProductBySku(productSearchSku.trim());
      if (res.status.code === 200 && res.data) {
        setSearchedProduct(res.data);
        setAvailablePricingModes(res.data.pricingModes || { dropshipping: true, wholesale: true });
        setShowVariantSelector(true);

        // Update order type based on available modes
        if (!res.data.pricingModes.dropshipping && res.data.pricingModes.wholesale) {
          setFormData(prev => ({ ...prev, orderType: 'wholesale' }));
        } else if (res.data.pricingModes.dropshipping && !res.data.pricingModes.wholesale) {
          setFormData(prev => ({ ...prev, orderType: 'dropshipping' }));
        }

        // Initialize quantities for all variants
        const initialQuantities = {};
        if (res.data.product.variations) {
          res.data.product.variations.forEach((v, idx) => {
            initialQuantities[idx] = 0;
          });
        }
        setVariantQuantities(initialQuantities);

        // Set initial pricing mode tab
        if (!res.data.pricingModes.dropshipping && res.data.pricingModes.wholesale) {
          setSelectedPricingMode('wholesale');
        } else {
          setSelectedPricingMode('dropshipping');
        }

        // Show source message
        const sourceMsg =
          res.data.source === 'db'
            ? 'Product found from Database'
            : res.data.source === 'alibaba_api_saved'
              ? 'Product found from API and saved to Database'
              : 'Product found from API';
        message.showToast(sourceMsg, ToastTypes.SUCCESS);
      } else {
        message.showToast('Product not found', ToastTypes.ERROR);
        setSearchedProduct(null);
        setShowVariantSelector(false);
      }
    } catch (err) {
      message.showToast(err?.message || 'Error searching product', ToastTypes.ERROR);
      setSearchedProduct(null);
      setShowVariantSelector(false);
    } finally {
      setSearchingProduct(false);
    }
  };

  // Update variant quantity
  const updateVariantQuantity = (variantIndex, change) => {
    setVariantQuantities(prev => {
      const currentQty = prev[variantIndex] || 0;
      const newQty = Math.max(0, currentQty + change);
      return {
        ...prev,
        [variantIndex]: newQty,
      };
    });
  };

  // Select variant and auto-fill with quantity
  const selectVariant = (variant, variantIndex, quantity = 1) => {
    const newOrderDetails = [...formData.orderDetails];
    const qty = quantity || variantQuantities[variantIndex] || 1;
    const variantSkuId = variant.skuId?.toString() || '';

    // Set variant.finalPrice based on selected pricing mode
    const variantFinalPrice = selectedPricingMode === 'wholesale' ? variant.wholesaleFinalPrice || 0 : variant.finalPrice || 0;

    // Check if same variant already exists with the SAME pricing mode
    // We check both skuId and pricingMode to determine if it's the same variant+mode combination
    const existingItemIndex = newOrderDetails.findIndex(item => {
      if (!item.sku || !item.variant) return false;
      const itemSkuId = item.sku?.toString() || item.variant?.skuId?.toString() || '';
      if (itemSkuId !== variantSkuId) return false;

      // Check if pricing mode matches
      // If item has pricingMode stored, compare directly
      if (item.variant.pricingMode) {
        return item.variant.pricingMode === selectedPricingMode;
      }

      // Fallback: Check by comparing finalPrice with expected price for current mode
      // This handles old items that don't have pricingMode set
      const itemFinalPrice = item.variant?.finalPrice || 0;
      const expectedPriceForMode = selectedPricingMode === 'wholesale' ? variant.wholesaleFinalPrice || 0 : variant.finalPrice || 0;

      // If prices match, it's the same variant+mode combination
      return Math.abs(itemFinalPrice - expectedPriceForMode) < 0.01; // Use small tolerance for float comparison
    });

    if (existingItemIndex !== -1) {
      // Same variant with same pricing mode exists - update quantity
      newOrderDetails[existingItemIndex].quantity = (newOrderDetails[existingItemIndex].quantity || 0) + qty;
      message.showToast(`Quantity updated: +${qty} added to existing variant`, ToastTypes.SUCCESS);
    } else {
      // Find first empty item (SKU is empty or null)
      const emptyItemIndex = newOrderDetails.findIndex(item => !item.sku || item.sku.trim() === '');

      if (emptyItemIndex !== -1) {
        // Fill first empty item
        newOrderDetails[emptyItemIndex] = {
          orderItemId: '',
          sku: variantSkuId,
          quantity: qty,
          consumerPrice: '', // Don't auto-fill consumerPrice
          variant: {
            skuId: variantSkuId,
            actualPrice: variant.actualPrice || 0, // Actual price (ap only)
            weight: variant.weight || 0,
            length: variant.length || 0,
            width: variant.width || 0,
            height: variant.height || 0,
            ls: variant.ls || 0,
            finalPrice: variantFinalPrice, // Based on selected pricing mode
            wholesaleFinalPrice: variant.wholesaleFinalPrice || 0,
            consignPrice: variant.consignPrice || 0, // Consign price (cp or consignPrice)
            pricingMode: selectedPricingMode, // Store the pricing mode for this item
            adminCustomPrice: '', // Admin can manually override the price
          },
        };
        message.showToast(`Variant added to Item ${emptyItemIndex + 1} with quantity ${qty}`, ToastTypes.SUCCESS);
      } else {
        // No empty item - add as new item
        newOrderDetails.push({
          orderItemId: '',
          sku: variantSkuId,
          quantity: qty,
          consumerPrice: '', // Don't auto-fill consumerPrice
          variant: {
            skuId: variantSkuId,
            actualPrice: variant.actualPrice || 0, // Actual price (ap only)
            weight: variant.weight || 0,
            length: variant.length || 0,
            width: variant.width || 0,
            height: variant.height || 0,
            ls: variant.ls || 0,
            finalPrice: variantFinalPrice, // Based on selected pricing mode
            wholesaleFinalPrice: variant.wholesaleFinalPrice || 0,
            consignPrice: variant.consignPrice || 0, // Consign price (cp or consignPrice)
            pricingMode: selectedPricingMode, // Store the pricing mode for this item
            adminCustomPrice: '', // Admin can manually override the price
          },
        });
        message.showToast(`Variant added to Item ${newOrderDetails.length} with quantity ${qty}`, ToastTypes.SUCCESS);
      }
    }

    setFormData(prev => ({ ...prev, orderDetails: newOrderDetails }));
    // Reset quantity for this variant after adding
    setVariantQuantities(prev => ({ ...prev, [variantIndex]: 0 }));
  };

  // Handle form submission
  const handleSubmit = async e => {
    e.preventDefault();

    // Validate required fields
    if (!formData.sellerId) {
      message.showToast('Please select a seller', ToastTypes.ERROR);
      return;
    }

    if (formData.orderDetails.length === 0) {
      message.showToast('Please add at least one order detail', ToastTypes.ERROR);
      return;
    }

    // Validate order details
    for (let i = 0; i < formData.orderDetails.length; i++) {
      const detail = formData.orderDetails[i];
      if (!detail.sku) {
        message.showToast(`Order detail ${i + 1}: SKU is required`, ToastTypes.ERROR);
        return;
      }
      if (!detail.quantity || detail.quantity < 1) {
        message.showToast(`Order detail ${i + 1}: Valid quantity is required`, ToastTypes.ERROR);
        return;
      }
    }

    // Prepare data for submission
    const submitData = {
      ...formData,
    };

    // Remove UI-only fields
    delete submitData.sellerName;
    delete submitData.sellerEmail;

    // Convert numeric fields (only if they have values)
    if (formData.totalShippingCharges) {
      submitData.totalShippingCharges = Number(formData.totalShippingCharges);
    } else {
      delete submitData.totalShippingCharges;
    }

    if (formData.gstOnShipping) {
      submitData.gstOnShipping = Number(formData.gstOnShipping);
    } else {
      delete submitData.gstOnShipping;
    }

    if (formData.mkp_current_total_price) {
      submitData.mkp_current_total_price = Number(formData.mkp_current_total_price);
    } else {
      delete submitData.mkp_current_total_price;
    }

    if (formData.mkp_current_total_tax) {
      submitData.mkp_current_total_tax = Number(formData.mkp_current_total_tax);
    } else {
      delete submitData.mkp_current_total_tax;
    }

    if (formData.mkp_current_subtotal_price) {
      submitData.mkp_current_subtotal_price = Number(formData.mkp_current_subtotal_price);
    } else {
      delete submitData.mkp_current_subtotal_price;
    }

    // Convert order details
    submitData.orderDetails = formData.orderDetails.map(detail => {
      const cleanDetail = {
        sku: detail.sku,
        quantity: Number(detail.quantity),
      };

      if (detail.orderItemId) cleanDetail.orderItemId = detail.orderItemId;
      if (detail.consumerPrice) cleanDetail.consumerPrice = Number(detail.consumerPrice);

      // Only include variant if it has meaningful data
      if (detail.variant && (detail.variant.skuId || detail.variant.actualPrice || detail.variant.weight || detail.variant.finalPrice || detail.variant.adminCustomPrice)) {
        cleanDetail.variant = {};
        if (detail.variant.skuId) cleanDetail.variant.skuId = detail.variant.skuId;
        if (detail.variant.actualPrice) cleanDetail.variant.actualPrice = Number(detail.variant.actualPrice);
        if (detail.variant.weight) cleanDetail.variant.weight = Number(detail.variant.weight);
        if (detail.variant.ls) cleanDetail.variant.ls = detail.variant.ls;
        
        // PRIORITY: Check if admin custom price is provided first
        const hasCustomPrice = detail.variant.adminCustomPrice && 
                               String(detail.variant.adminCustomPrice).trim() !== '' && 
                               !isNaN(Number(detail.variant.adminCustomPrice)) &&
                               Number(detail.variant.adminCustomPrice) > 0;
        
        if (hasCustomPrice) {
          // Use custom price for both dropshipping (finalPrice) and wholesale (wholesaleFinalPrice)
          const customPrice = Number(detail.variant.adminCustomPrice);
          cleanDetail.variant.finalPrice = customPrice;
          cleanDetail.variant.wholesaleFinalPrice = customPrice;
          cleanDetail.variant.adminCustomPrice = customPrice; // Also save for reference
        } else {
          // Use original variant prices
          if (detail.variant.finalPrice) cleanDetail.variant.finalPrice = Number(detail.variant.finalPrice);
          if (detail.variant.wholesaleFinalPrice) cleanDetail.variant.wholesaleFinalPrice = Number(detail.variant.wholesaleFinalPrice);
        }
      }

      return cleanDetail;
    });

    // Convert breakdown (keep empty array if no items)
    if (formData.breakdown && formData.breakdown.length > 0) {
      submitData.breakdown = formData.breakdown.map(item => {
        const cleanItem = {};
        if (item.productId) cleanItem.productId = item.productId;
        if (item.pricingMode) cleanItem.pricingMode = item.pricingMode;
        if (item.LP) cleanItem.LP = Number(item.LP);
        if (item.LC) cleanItem.LC = Number(item.LC);
        if (item.LS) cleanItem.LS = Number(item.LS);
        if (item.GST_PR) cleanItem.GST_PR = Number(item.GST_PR);
        if (item.GST_FR) cleanItem.GST_FR = Number(item.GST_FR);
        if (item.FBP) cleanItem.FBP = Number(item.FBP);
        return cleanItem;
      });
    } else {
      submitData.breakdown = [];
    }

    // Clean up empty strings for optional text fields (but keep required fields)
    const optionalFields = ['transactionId', 'orderId', 'marketplace', 'mkp_financial_status'];
    Object.keys(submitData).forEach(key => {
      // Only delete empty strings for optional fields, not required ones
      if (submitData[key] === '' && optionalFields.includes(key)) {
        delete submitData[key];
      }
    });

    // Debug: Log the order details to verify custom prices
    console.log('📦 Creating Manual Order with data:', {
      orderDetails: submitData.orderDetails.map(d => ({
        sku: d.sku,
        quantity: d.quantity,
        variantFinalPrice: d.variant?.finalPrice,
        adminCustomPrice: d.variant?.adminCustomPrice,
      }))
    });

    loader.showInlineLoader('Creating manual order...', 'medium');

    try {
      const res = await orderApiService.createManualOrder(submitData);

      if (res.status.code === 200) {
        message.showToast('Order created successfully!', ToastTypes.SUCCESS);
        if (onSuccess) onSuccess();
        handleClose();

        // Navigate to order detail page using MongoDB _id
        if (res.data.order && res.data.order._id) {
          navigate(`/admin/order-management/details/${res.data.order._id}`);
        } else if (res.data.orderId) {
          // Fallback: try to get order by orderId if _id not available
          navigate(`/admin/order-management`);
        }
      } else {
        message.showToast(res.error.message || 'Failed to create order', ToastTypes.ERROR);
      }
    } catch (err) {
      message.showToast(err?.message || 'Server error. Try again later', ToastTypes.ERROR);
    } finally {
      loader.hideLoader();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="manual-order-modal-overlay">
      <div className="manual-order-modal">
        <div className="manual-order-header">
          <h2>Add Manual Order</h2>
          <button className="manual-order-close-btn" onClick={handleClose} type="button">
            <FiX size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="manual-order-form">
          {/* Product Search Section */}
          <div className="manual-order-form-section" style={{ backgroundColor: '#f8f9fa', border: '1px solid #e0e0e0' }}>
            <h3 className="manual-order-section-title" style={{ marginBottom: '10px' }}>
              <i className="fas fa-search"></i> Search Product by SKU
            </h3>
            <div className="manual-order-product-search-container" style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <input
                  type="text"
                  value={productSearchSku}
                  onChange={e => setProductSearchSku(e.target.value)}
                  onKeyPress={e => e.key === 'Enter' && handleProductSearch()}
                  placeholder="Enter product SKU or Offer ID"
                  style={{
                    width: '100%',
                    padding: '6px 8px',
                    fontSize: '0.8rem',
                    height: '32px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                  }}
                />
              </div>
              <button
                type="button"
                className="manual-order-search-btn"
                onClick={handleProductSearch}
                disabled={searchingProduct || !productSearchSku.trim()}
                style={{
                  padding: '6px 16px',
                  fontSize: '0.8rem',
                  height: '32px',
                  backgroundColor: '#667eea',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: searchingProduct ? 'not-allowed' : 'pointer',
                  opacity: searchingProduct ? 0.6 : 1,
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                }}
              >
                {searchingProduct ? 'Searching...' : 'Search'}
              </button>
            </div>

            {searchedProduct && showVariantSelector && (
              <div className="manual-order-product-card" style={{ marginTop: '12px', padding: '12px', backgroundColor: '#fff', borderRadius: '4px', border: '1px solid #ddd' }}>
                <div className="manual-order-product-header" style={{ marginBottom: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <strong style={{ fontSize: '0.85rem' }}>{searchedProduct.product.title}</strong>
                    <div style={{ fontSize: '0.75rem', color: '#666', marginTop: '2px' }}>SKU: {searchedProduct.product.sku}</div>
                    {searchedProduct.source && (
                      <div style={{ fontSize: '0.7rem', color: '#667eea', marginTop: '2px', fontWeight: '500' }}>
                        {searchedProduct.source === 'db'
                          ? '✓ Found from Database'
                          : searchedProduct.source === 'alibaba_api_saved'
                            ? '✓ Found from API (saved to DB)'
                            : '✓ Found from API'}
                      </div>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setShowVariantSelector(false);
                      setSearchedProduct(null);
                      setProductSearchSku('');
                      setVariantQuantities({});
                      setSelectedPricingMode('dropshipping');
                    }}
                    style={{
                      padding: '4px 8px',
                      fontSize: '0.75rem',
                      backgroundColor: '#f0f0f0',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      cursor: 'pointer',
                    }}
                  >
                    Close
                  </button>
                </div>

                {/* Pricing Mode Tabs */}
                <div
                  className="manual-order-pricing-tabs"
                  style={{
                    display: 'flex',
                    gap: '8px',
                    marginBottom: '12px',
                    borderBottom: '2px solid #e0e0e0',
                    paddingBottom: '8px',
                  }}
                >
                  <button
                    type="button"
                    onClick={() => {
                      if (!availablePricingModes.dropshipping) {
                        message.showToast('Dropshipping is not available for this product', ToastTypes.WARNING);
                        return;
                      }
                      setSelectedPricingMode('dropshipping');
                      setFormData(prev => ({ ...prev, orderType: 'dropshipping' }));
                      updateItemsForPricingMode('dropshipping');
                    }}
                    style={{
                      padding: '6px 16px',
                      fontSize: '0.8rem',
                      fontWeight: '600',
                      border: 'none',
                      borderRadius: '4px 4px 0 0',
                      cursor: availablePricingModes.dropshipping ? 'pointer' : 'not-allowed',
                      backgroundColor: selectedPricingMode === 'dropshipping' && availablePricingModes.dropshipping ? '#667eea' : 'transparent',
                      color: selectedPricingMode === 'dropshipping' && availablePricingModes.dropshipping ? '#fff' : availablePricingModes.dropshipping ? '#666' : '#999',
                      borderBottom: selectedPricingMode === 'dropshipping' && availablePricingModes.dropshipping ? '2px solid #667eea' : 'none',
                      marginBottom: selectedPricingMode === 'dropshipping' && availablePricingModes.dropshipping ? '-2px' : '0',
                      opacity: availablePricingModes.dropshipping ? 1 : 0.6,
                    }}
                  >
                    Dropshipping
                  </button>
                  {availablePricingModes.wholesale && (
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedPricingMode('wholesale');
                        setFormData(prev => ({ ...prev, orderType: 'wholesale' }));
                        updateItemsForPricingMode('wholesale');
                      }}
                      style={{
                        padding: '6px 16px',
                        fontSize: '0.8rem',
                        fontWeight: '600',
                        border: 'none',
                        borderRadius: '4px 4px 0 0',
                        cursor: 'pointer',
                        backgroundColor: selectedPricingMode === 'wholesale' ? '#667eea' : 'transparent',
                        color: selectedPricingMode === 'wholesale' ? '#fff' : '#666',
                        borderBottom: selectedPricingMode === 'wholesale' ? '2px solid #667eea' : 'none',
                        marginBottom: selectedPricingMode === 'wholesale' ? '-2px' : '0',
                      }}
                    >
                      Wholesale
                    </button>
                  )}
                </div>

                {/* Variant List */}
                <div className="manual-order-variant-list-container" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                  <div style={{ fontSize: '0.75rem', fontWeight: '600', marginBottom: '8px', color: '#333' }}>
                    Variants ({searchedProduct.product.variations?.length || 0} available):
                  </div>
                  {searchedProduct.product.variations && searchedProduct.product.variations.length > 0 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {searchedProduct.product.variations.map((variant, vIndex) => {
                        // Get variant image
                        const variantImage = variant.skuAttributes?.find(attr => attr.skuImageUrl)?.skuImageUrl || searchedProduct.product.imgUrl?.[0] || null;

                        // Get variant name from attributes (e.g., "Black numbers")
                        const variantName = variant.skuAttributes?.map(attr => attr.valueTrans || attr.value).join(' ') || `SKU ${variant.skuId}`;

                        // Get price based on selected mode
                        const displayPrice = selectedPricingMode === 'dropshipping' ? variant.finalPrice || 0 : variant.wholesaleFinalPrice || 0;

                        return (
                          <div
                            key={vIndex}
                            className="manual-order-variant-card"
                            style={{
                              padding: '10px',
                              border: '1px solid #e0e0e0',
                              borderRadius: '4px',
                              backgroundColor: '#fafafa',
                            }}
                          >
                            {/* Variant Image */}
                            {variantImage && (
                              <img
                                src={variantImage}
                                alt={variantName}
                                className="manual-order-variant-image"
                                style={{
                                  width: '50px',
                                  height: '50px',
                                  objectFit: 'cover',
                                  borderRadius: '4px',
                                  border: '1px solid #e0e0e0',
                                  flexShrink: 0,
                                }}
                                onError={e => {
                                  e.target.style.display = 'none';
                                }}
                              />
                            )}

                            {/* Title and Dimensions */}
                            <div className="manual-order-variant-title-section">
                              <div className="manual-order-variant-title" style={{ fontSize: '0.8rem', fontWeight: '600', marginBottom: '4px' }}>
                                {variantName}
                              </div>
                              <div className="manual-order-variant-dimensions" style={{ fontSize: '0.7rem', color: '#666' }}>
                                Wt: {(variant.weight || 0).toFixed(3)}kg | L: {(variant.length || 0).toFixed(2)}cm | W: {(variant.width || 0).toFixed(2)}cm | H:{' '}
                                {(variant.height || 0).toFixed(2)}cm
                              </div>
                            </div>

                            {/* Price and Unit */}
                            <div className="manual-order-variant-price-section">
                              <div style={{ fontSize: '0.75rem', fontWeight: '600', color: '#667eea' }}>₹{displayPrice.toFixed(2)} /</div>
                              <div style={{ fontSize: '0.7rem', color: '#888', marginTop: '2px' }}>{variant.amountOnSale || 0} units in stock</div>
                            </div>

                            {/* Quantity Selector and Add Button */}
                            <div className="manual-order-variant-actions">
                              <button
                                type="button"
                                onClick={e => {
                                  e.stopPropagation();
                                  updateVariantQuantity(vIndex, -1);
                                }}
                                disabled={(variantQuantities[vIndex] || 0) <= 0}
                                style={{
                                  width: '28px',
                                  height: '28px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  border: '1px solid #ddd',
                                  borderRadius: '4px',
                                  backgroundColor: '#fff',
                                  cursor: (variantQuantities[vIndex] || 0) <= 0 ? 'not-allowed' : 'pointer',
                                  fontSize: '0.9rem',
                                  fontWeight: '600',
                                  color: '#666',
                                  opacity: (variantQuantities[vIndex] || 0) <= 0 ? 0.5 : 1,
                                }}
                              >
                                −
                              </button>
                              <input
                                type="number"
                                value={variantQuantities[vIndex] || 0}
                                onChange={e => {
                                  const val = parseInt(e.target.value) || 0;
                                  setVariantQuantities(prev => ({ ...prev, [vIndex]: Math.max(0, val) }));
                                }}
                                min="0"
                                style={{
                                  width: '45px',
                                  textAlign: 'center',
                                  fontSize: '0.8rem',
                                  fontWeight: '500',
                                  border: '1px solid #ddd',
                                  borderRadius: '4px',
                                  padding: '4px',
                                  outline: 'none',
                                }}
                              />
                              <button
                                type="button"
                                onClick={e => {
                                  e.stopPropagation();
                                  updateVariantQuantity(vIndex, 1);
                                }}
                                disabled={(variantQuantities[vIndex] || 0) >= (variant.amountOnSale || 0)}
                                style={{
                                  width: '28px',
                                  height: '28px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  border: '1px solid #ddd',
                                  borderRadius: '4px',
                                  backgroundColor: '#fff',
                                  cursor: (variantQuantities[vIndex] || 0) >= (variant.amountOnSale || 0) ? 'not-allowed' : 'pointer',
                                  fontSize: '0.9rem',
                                  fontWeight: '600',
                                  color: '#666',
                                  opacity: (variantQuantities[vIndex] || 0) >= (variant.amountOnSale || 0) ? 0.5 : 1,
                                }}
                              >
                                +
                              </button>
                              <button
                                type="button"
                                className="manual-order-variant-add-btn"
                                onClick={e => {
                                  e.stopPropagation();
                                  const qty = variantQuantities[vIndex] || 0;
                                  if (qty > 0) {
                                    selectVariant(variant, vIndex, qty);
                                  } else {
                                    message.showToast('Please select quantity', ToastTypes.WARNING);
                                  }
                                }}
                                disabled={(variantQuantities[vIndex] || 0) <= 0}
                                style={{
                                  padding: '6px 14px',
                                  fontSize: '0.75rem',
                                  backgroundColor: (variantQuantities[vIndex] || 0) > 0 ? '#667eea' : '#ccc',
                                  color: '#fff',
                                  border: 'none',
                                  borderRadius: '4px',
                                  cursor: (variantQuantities[vIndex] || 0) > 0 ? 'pointer' : 'not-allowed',
                                  whiteSpace: 'nowrap',
                                  fontWeight: '500',
                                }}
                              >
                                Add
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div style={{ fontSize: '0.75rem', color: '#666' }}>No variants available</div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Order Details Section */}
          <div className="manual-order-form-section">
            <h3 className="manual-order-section-title">
              <i className="fas fa-box"></i> Order Details *
            </h3>

            {formData.orderDetails.map((detail, index) => (
              <div key={index} className="manual-order-order-detail-item">
                <div className="manual-order-item-header">
                  <span>Item {index + 1}</span>
                  {formData.orderDetails.length > 1 && (
                    <button type="button" className="manual-order-remove-item-btn" onClick={() => removeOrderDetail(index)}>
                      <FiX size={10} />
                    </button>
                  )}
                </div>

                <div className="manual-order-detail-row">
                  <div className="manual-order-form-group">
                    <label title="Order Item ID">Item ID</label>
                    <input type="text" value={detail.orderItemId} onChange={e => handleOrderDetailChange(index, 'orderItemId', e.target.value)} placeholder="Opt" title="Optional" />
                  </div>
                  <div className="manual-order-form-group">
                    <label> Variant SKU </label>
                    <input type="text" value={detail.sku} onChange={e => handleOrderDetailChange(index, 'sku', e.target.value)} placeholder="Required" required readOnly />
                  </div>
                  <div className="manual-order-form-group">
                    <label>Qty *</label>
                    <input type="number" value={detail.quantity} onChange={e => handleOrderDetailChange(index, 'quantity', e.target.value)} min="1" required />
                  </div>
                  <div className="manual-order-form-group">
                    <label title="Consumer Price">Consumer Price</label>
                    <input type="number" value={detail.consumerPrice} onChange={e => handleOrderDetailChange(index, 'consumerPrice', e.target.value)} step="0.01" />
                  </div>
                  <div className="manual-order-form-group">
                    <label title="Original variant price">Auto ₹</label>
                    <input
                      type="number"
                      value={detail.variant?.finalPrice || ''}
                      step="0.01"
                      readOnly
                      style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                      title="Original variant price"
                    />
                  </div>
                  <div className="manual-order-form-group">
                    <label title="Custom price overrides auto price">Custom Price</label>
                    <input
                      type="number"
                      value={detail.variant?.adminCustomPrice !== undefined ? detail.variant.adminCustomPrice : ''}
                      onChange={e => handleOrderDetailChange(index, 'variant.adminCustomPrice', e.target.value)}
                      step="0.01"
                      placeholder="enter custom price"
                      style={{ 
                        borderColor: detail.variant?.adminCustomPrice ? '#28a745' : '#667eea', 
                        borderWidth: '2px', 
                        fontWeight: '600', 
                        fontSize: '0.8rem',
                        backgroundColor: detail.variant?.adminCustomPrice ? '#f0fff4' : 'white'
                      }}
                      title="Enter custom price to override auto price"
                    />
                    {detail.variant?.adminCustomPrice && (
                      <small style={{ fontSize: '0.6rem', color: '#28a745', fontWeight: '600' }}>
                        ✓ ₹{Number(detail.variant.adminCustomPrice).toFixed(0)}
                      </small>
                    )}
                  </div>
                </div>
              </div>
            ))}

            <button type="button" className="manual-order-add-item-btn" onClick={addOrderDetail}>
              <i className="fas fa-plus"></i> Add Order Detail
            </button>
          </div>

          {/* Order Information Section */}
          <div className="manual-order-form-section">
            <h3 className="manual-order-section-title">
              <i className="fas fa-info-circle"></i> Order Information
            </h3>
            <div className="manual-order-form-grid">
              <div className="manual-order-form-group">
                <label>Order ID</label>
                <input type="text" name="orderId" value={formData.orderId} onChange={handleInputChange} placeholder="Leave empty to auto-generate" />
              </div>
              <div className="manual-order-form-group">
                <label>Marketplace</label>
                <input type="text" name="marketplace" value={formData.marketplace} onChange={handleInputChange} placeholder="e.g., Amazon, Shopify, Manual" readOnly />
              </div>
              <div className="manual-order-form-group">
                <label>Status *</label>
                <select name="status" value={formData.status} onChange={handleInputChange}>
                  <option value="abnormal">Abnormal</option>
                  <option value="payment pending">Payment Pending</option>
                  <option value="payment paid">Payment Paid</option>
                  <option value="processing">Processing</option>
                  <option value="order processed to supplier">Order Processed to Supplier</option>
                  <option value="partially-shipped">Partially Shipped</option>
                  <option value="shipped">Shipped</option>
                  <option value="delivered">Delivered</option>
                  <option value="cancel">Cancel</option>
                  <option value="refunded">Refunded</option>
                </select>
              </div>
              <div className="manual-order-form-group">
                <label>Order Date</label>
                <input type="date" name="orderDate" value={formData.orderDate} onChange={handleInputChange} />
              </div>
              <div className="manual-order-form-group">
                <label>Paid Date</label>
                <input type="date" name="paidDate" value={formData.paidDate} onChange={handleInputChange} />
              </div>
              {searchedProduct && (
                <div className="manual-order-form-group">
                  <label>Order Type *</label>
                  <select name="orderType" value={formData.orderType} onChange={handleInputChange}>
                    {availablePricingModes.dropshipping && <option value="dropshipping">Dropshipping</option>}
                    {availablePricingModes.wholesale && <option value="wholesale">Wholesale</option>}
                    {!availablePricingModes.dropshipping && !availablePricingModes.wholesale && <option value="dropshipping">Dropshipping</option>}
                  </select>
                  {searchedProduct && (
                    <small style={{ fontSize: '0.7rem', color: '#666', marginTop: '2px', display: 'block' }}>
                      {!availablePricingModes.dropshipping && availablePricingModes.wholesale && 'Product only supports wholesale'}
                      {availablePricingModes.dropshipping && !availablePricingModes.wholesale && 'Product only supports dropshipping'}
                      {availablePricingModes.dropshipping && availablePricingModes.wholesale && 'Both modes available'}
                    </small>
                  )}
                </div>
              )}
            </div>

            <div className="manual-order-form-group-inline">
              <label className="manual-order-checkbox-label">
                <input type="checkbox" name="ticket" checked={formData.ticket} onChange={handleInputChange} />
                <span>Has Ticket</span>
              </label>
              <label className="manual-order-checkbox-label">
                <input type="checkbox" name="orderExport" checked={formData.orderExport} onChange={handleInputChange} />
                <span>Order Export</span>
              </label>
              <label className="manual-order-checkbox-label">
                <input type="checkbox" name="fromFailedOrder" checked={formData.fromFailedOrder} onChange={handleInputChange} />
                <span>From Failed Order</span>
              </label>
            </div>
          </div>

          {/* Seller Selection Section */}
          <div className="manual-order-form-section">
            <h3 className="manual-order-section-title">
              <i className="fas fa-user"></i> Seller Selection *
            </h3>

            {!formData.sellerId ? (
              <div className="manual-order-seller-search-container">
                <div className="manual-order-form-group">
                  <label>Search Seller</label>
                  <div className="manual-order-search-input-wrapper">
                    <input
                      type="text"
                      value={sellerSearchQuery}
                      onChange={e => handleSellerSearch(e.target.value)}
                      onFocus={() => setShowSellerSearch(true)}
                      placeholder="Search by name or email..."
                      className="manual-order-seller-search-input"
                    />
                    <i className="fas fa-search manual-order-search-icon"></i>
                  </div>
                </div>

                {showSellerSearch && (
                  <div className="manual-order-seller-search-results">
                    {searchingUsers ? (
                      <div className="manual-order-search-loading">Searching...</div>
                    ) : sellerSearchResults.length > 0 ? (
                      sellerSearchResults.map(user => (
                        <div key={user._id} className="manual-order-seller-result-item" onClick={() => selectSeller(user)}>
                          <div className="manual-order-seller-info">
                            <span className="manual-order-seller-name">{user.firstName || user.userName || 'No name'}</span>
                            <span className="manual-order-seller-email">{user.email}</span>
                          </div>
                          {user.role && <span className="manual-order-seller-role">{user.role}</span>}
                        </div>
                      ))
                    ) : sellerSearchQuery.length >= 2 ? (
                      <div className="manual-order-no-results">No users found</div>
                    ) : (
                      <div className="manual-order-no-results">Type at least 2 characters to search</div>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div className="manual-order-selected-seller">
                <div className="manual-order-seller-card">
                  <div className="manual-order-seller-details">
                    <i className="fas fa-user-circle"></i>
                    <div>
                      <div className="manual-order-seller-name">{formData.sellerName}</div>
                      <div className="manual-order-seller-email">{formData.sellerEmail}</div>
                    </div>
                  </div>
                  <button type="button" className="manual-order-clear-seller-btn" onClick={clearSeller}>
                    <FiX size={10} />
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Shipping Address Section */}
          <div className="manual-order-form-section">
            <h3 className="manual-order-section-title">
              <i className="fas fa-shipping-fast"></i> Shipping Address
            </h3>
            <div className="manual-order-form-grid">
              <div className="manual-order-form-group">
                <label>Recipient Name</label>
                <input type="text" name="recipientName" value={formData.recipientName} onChange={handleInputChange} />
              </div>
              <div className="manual-order-form-group">
                <label>Phone Number</label>
                <input type="text" name="shippingPhoneNumber" value={formData.shippingPhoneNumber} onChange={handleInputChange} />
              </div>
              <div className="manual-order-form-group">
                <label>Address Line 1</label>
                <input type="text" name="shippingAddress1" value={formData.shippingAddress1} onChange={handleInputChange} />
              </div>
              <div className="manual-order-form-group">
                <label>Address Line 2</label>
                <input type="text" name="shippingAddress2" value={formData.shippingAddress2} onChange={handleInputChange} />
              </div>
              <div className="manual-order-form-group">
                <label>City</label>
                <input type="text" name="shippingCity" value={formData.shippingCity} onChange={handleInputChange} />
              </div>
              <div className="manual-order-form-group">
                <label>State</label>
                <input type="text" name="shippingState" value={formData.shippingState} onChange={handleInputChange} />
              </div>
              <div className="manual-order-form-group">
                <label>Postal Code</label>
                <input type="text" name="shippingPostalCode" value={formData.shippingPostalCode} onChange={handleInputChange} />
              </div>
              <div className="manual-order-form-group">
                <label>Shipping Charges</label>
                <input type="number" name="totalShippingCharges" value={formData.totalShippingCharges} onChange={handleInputChange} step="0.01" />
              </div>
              {/* Hide GST on Shipping for now */}
              {/* <div className="manual-order-form-group">
                <label>GST on Shipping</label>
                <input type="number" name="gstOnShipping" value={formData.gstOnShipping} onChange={handleInputChange} step="0.01" />
              </div> */}
            </div>
          </div>

          {/* Billing Address Section */}
          <div className="manual-order-form-section">
            <h3 className="manual-order-section-title">
              <i className="fas fa-file-invoice"></i> Billing Address
            </h3>

            <label className="manual-order-checkbox-label">
              <input type="checkbox" name="useSameAddressForBilling" checked={formData.useSameAddressForBilling} onChange={handleInputChange} />
              <span>Same as shipping address</span>
            </label>

            {!formData.useSameAddressForBilling && (
              <div className="manual-order-form-grid">
                <div className="manual-order-form-group">
                  <label>Billing Name</label>
                  <input type="text" name="billingName" value={formData.billingName} onChange={handleInputChange} />
                </div>
                <div className="manual-order-form-group">
                  <label>Billing Phone</label>
                  <input type="text" name="billingPhone" value={formData.billingPhone} onChange={handleInputChange} />
                </div>
                <div className="manual-order-form-group full-width">
                  <label>Address Line 1</label>
                  <input type="text" name="billingAddress1" value={formData.billingAddress1} onChange={handleInputChange} />
                </div>
                <div className="manual-order-form-group full-width">
                  <label>Address Line 2</label>
                  <input type="text" name="billingAddress2" value={formData.billingAddress2} onChange={handleInputChange} />
                </div>
                <div className="manual-order-form-group">
                  <label>City</label>
                  <input type="text" name="billingCity" value={formData.billingCity} onChange={handleInputChange} />
                </div>
                <div className="manual-order-form-group">
                  <label>State</label>
                  <input type="text" name="billingState" value={formData.billingState} onChange={handleInputChange} />
                </div>
                <div className="manual-order-form-group">
                  <label>Postal Code</label>
                  <input type="text" name="billingPostalCode" value={formData.billingPostalCode} onChange={handleInputChange} />
                </div>
              </div>
            )}
          </div>

          {/* Order Status History Section */}
          <div className="manual-order-form-section">
            <h3 className="manual-order-section-title">
              <i className="fas fa-history"></i> Order Status History
            </h3>

            {formData.orderStatus.map((status, index) => (
              <div key={index} className="manual-order-status-item">
                <div className="manual-order-item-header">
                  <span>Status {index + 1}</span>
                  {formData.orderStatus.length > 1 && (
                    <button type="button" className="manual-order-remove-item-btn" onClick={() => removeOrderStatus(index)}>
                      <FiX size={10} />
                    </button>
                  )}
                </div>

                <div className="manual-order-form-grid">
                  <div className="manual-order-form-group">
                    <label>Status</label>
                    <select value={status.status} onChange={e => handleOrderStatusChange(index, 'status', e.target.value)}>
                      <option value="abnormal">Abnormal</option>
                      <option value="payment pending">Payment Pending</option>
                      <option value="payment paid">Payment Paid</option>
                      <option value="processing">Processing</option>
                      <option value="shipped">Shipped</option>
                      <option value="delivered">Delivered</option>
                      <option value="cancel">Cancel</option>
                    </select>
                  </div>
                  <div className="manual-order-form-group">
                    <label>Date</label>
                    <input
                      type="datetime-local"
                      value={status.date ? status.date.slice(0, 16) : ''}
                      onChange={e => handleOrderStatusChange(index, 'date', new Date(e.target.value).toISOString())}
                    />
                  </div>
                </div>
              </div>
            ))}

            <button type="button" className="manual-order-add-item-btn" onClick={addOrderStatus}>
              <i className="fas fa-plus"></i> Add Status Entry
            </button>
          </div>

          {/* Marketplace Fields Section */}
          <div className="manual-order-form-section">
            <h3 className="manual-order-section-title">
              <i className="fas fa-store"></i> Marketplace Fields (Optional)
            </h3>
            <div className="manual-order-form-grid">
              <div className="manual-order-form-group">
                <label>Total Price</label>
                <input type="number" name="mkp_current_total_price" value={formData.mkp_current_total_price} onChange={handleInputChange} step="0.01" />
              </div>
              <div className="manual-order-form-group">
                <label>Financial Status</label>
                <input type="text" name="mkp_financial_status" value={formData.mkp_financial_status} onChange={handleInputChange} />
              </div>
              <div className="manual-order-form-group">
                <label>Total Tax</label>
                <input type="number" name="mkp_current_total_tax" value={formData.mkp_current_total_tax} onChange={handleInputChange} step="0.01" />
              </div>
              <div className="manual-order-form-group">
                <label>Subtotal Price</label>
                <input type="number" name="mkp_current_subtotal_price" value={formData.mkp_current_subtotal_price} onChange={handleInputChange} step="0.01" />
              </div>
            </div>
          </div>

          {/* Form Actions */}
          <div className="manual-order-form-actions">
            <button type="button" className="manual-order-btn-cancel" onClick={handleClose}>
              Cancel
            </button>
            <button type="submit" className="manual-order-btn-submit">
              <i className="fas fa-check"></i> Create Order
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ManualOrderForm;
